import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  TextFieldProps,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { currencyOptions, ICurrencyOption } from '../../../../../../constants';
import { TAmountOption } from './AddNewGroupTutorRateModal';
import GroupTutorRateVersionListDropdown, {
  TGroupTutorRateVersionOption,
} from '../../../../../../components/Inputs/admin/GroupTutorRateVersionListDropdown';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../components/Inputs';
import DarkLoadingButton, {
  defaultButtonStyles,
} from '../../../../../../components/V2/Buttons';

interface AddAmountModalProps {
  open: boolean;
  amountData?: TAmountOption;
  onClose: () => void;
  onAddAmount?: (amountData: TAmountOption) => void;
  onUpdateAmount?: (amountData: TAmountOption) => void;
}

type TState = {
  selectedRateVersion: TGroupTutorRateVersionOption | null;
  selectedCurrency: ICurrencyOption | null;
  amount: string;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialState: TState = {
  selectedRateVersion: null,
  selectedCurrency: null,
  amount: '',
};

const AddAmountModal: FunctionComponent<AddAmountModalProps> = ({
  open,
  amountData,
  onClose,
  onAddAmount,
  onUpdateAmount,
}) => {
  // State
  const [state, setState] = useState({ ...initialState });

  let mode: 'new' | 'edit' = 'new';

  if (amountData) {
    mode = 'edit';
  }

  const isSubmitDisabled = Boolean(
    !state.selectedRateVersion ||
      !state.selectedCurrency ||
      state.amount.trim().length < 1 ||
      (state.amount.trim().length && parseFloat(state.amount) <= 0)
  );

  useEffect(() => {
    if (mode !== 'new' && amountData) {
      setState((prevState) => {
        const newState = { ...prevState };

        if (amountData.amount) {
          newState.amount = amountData.amount.toString();
        }

        if (amountData.currency) {
          const _matchingItem = currencyOptions.find(
            (item) => item.value === amountData.currency
          );

          if (_matchingItem) {
            newState.selectedCurrency = { ..._matchingItem };
          }
        }

        if (amountData.version) {
          newState.selectedRateVersion = { ...amountData.version };
        }

        return newState;
      });
    }
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleSubmit = () => {
    if (mode === 'new') {
      if (state.selectedCurrency && state.selectedRateVersion && onAddAmount) {
        onAddAmount({
          version: state.selectedRateVersion,
          amount: parseFloat(state.amount),
          currency: state.selectedCurrency.value,
        });

        onClose();
      }
    } else if (mode === 'edit') {
      if (
        state.selectedCurrency &&
        state.selectedRateVersion &&
        onUpdateAmount
      ) {
        onUpdateAmount({
          version: state.selectedRateVersion,
          amount: parseFloat(state.amount),
          currency: state.selectedCurrency.value,
        });

        onClose();
      }
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        Add New Amount
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <div style={{ paddingTop: '6px' }}>
          <Grid container spacing="20px">
            <Grid item xs={12}>
              <FormControl fullWidth>
                <GroupTutorRateVersionListDropdown
                  selectedItem={state.selectedRateVersion}
                  handleChange={(event, value) =>
                    updateStateAttributes({
                      selectedRateVersion: value,
                    })
                  }
                  componentProps={{ fullWidth: true }}
                  textFieldProps={{
                    label: 'Rate Version',
                    placeholder: 'Select',
                    ...textFieldProps,
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  size="small"
                  value={state.selectedCurrency}
                  options={currencyOptions}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.value
                  }
                  onChange={(event, value) =>
                    updateStateAttributes({
                      selectedCurrency: value,
                    })
                  }
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Currency *"
                      placeholder="Currency"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  type="number"
                  label="Amount *"
                  placeholder="Enter here..."
                  value={state.amount}
                  onChange={(event) =>
                    updateStateAttributes({ amount: event.target.value })
                  }
                />
              </FormControl>
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
        <DarkLoadingButton
          disableElevation
          variant="contained"
          color="inherit"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          sx={{ ...defaultButtonStyles }}
        >
          Add
        </DarkLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddAmountModal;
